import React, { useCallback, useMemo, useState } from "react";
import { Image, Modal, ModalProps } from "react-bootstrap";
import { Tournament } from "@nestcoinco/hyper-api-gateway-api-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./UnableToPerformActionModal.scss";
import ImageSadEmoji from "../../../images/sad-emoji.png";
import ImageCloud from "../../../images/cloud.svg";
import ImageNoWifi from "../../../images/no-wifi.svg";
import HyperCoin from "../../HyperCoin/HyperCoin";
import FilledButton, {
  ButtonSizes,
} from "../../dashboard-page/FilledButton/FilledButton";
import useCountdown from "../../../hooks/useCountdown";
import { ROUTES, GamePlayEvents } from "../../../utils/constants";
import { useAtom } from "jotai";
import { duelEntryAtomWithStorage } from "../../../jotai/atoms/tournament";
import GameModal from "../GameModal/GameModal";
import { showErrorToast } from "../../../utils/show-toast";
import ImageFailedLaunch from "../../../images/failed-launch-icon.png";
import ImageSessionTimeout from "../../../images/session-timeout-icon.png";
import {
  gamePlayEventDataAtom,
  gamePlayEventNameAtom,
} from "../../../jotai/atoms/games";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";
import { FunctionWithVoidReturn } from "../../../utils/constants/generic-types";

export enum GameFailureActions {
  SUBMIT_SCORES = "submit-scores",
  LAUNCH_GAME = "launch-game",
  CONNECTION_LOST = "connection-lost",
}
interface UnableToPerformActionModalProps extends ModalProps {
  action?: GameFailureActions;
  tournament?: Tournament;
  refetchScores?: FunctionWithVoidReturn;
}

const TIME_ADDED = 30 * 60 * 1000; //30 minutes
const expirationDate = new Date(Date.now() + TIME_ADDED);
const expirationDateString = expirationDate.toISOString();

function UnableToPerformActionModal({
  tournament,
  action = GameFailureActions.SUBMIT_SCORES,
  refetchScores,
  ...restProps
}: UnableToPerformActionModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("games");
  const timeLeft = useCountdown({
    futureDateOrTotalTimeInMs: expirationDateString,
  });
  const [duelEntry] = useAtom(duelEntryAtomWithStorage);
  const lastGamePlayUrl = duelEntry?.gamePlayUrl;
  const [replay, setReplay] = useState(false);
  const [gamePlayEvent, setGamePlayEvent] = useAtom(gamePlayEventNameAtom);
  const [gamePlayEventData, setGamePlayEventData] = useAtom(
    gamePlayEventDataAtom
  );
  const { getCurrentContent } = useCurrentContent();
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const exitRoute =
    action === GameFailureActions.CONNECTION_LOST
      ? ROUTES.ROOT.DASHBOARD
      : ROUTES.GAMES.ROOT;
  const { title, content, heroImage, primaryCtaText, secondaryCtaText } =
    useMemo(
      () =>
        getCurrentContent({
          action,
          event: gamePlayEvent,
          data: {
            recommendedOrientation:
              gamePlayEventData?.data?.recommendedOrientation,
            recommendedPlatform: gamePlayEventData?.data?.recommendedPlatform,
          },
        }),
      [
        action,
        gamePlayEvent,
        gamePlayEventData?.data?.recommendedOrientation,
        gamePlayEventData?.data?.recommendedPlatform,
        getCurrentContent,
      ]
    );

  const resetGameEventState = useCallback(() => {
    setGamePlayEventData(undefined);
    setGamePlayEvent(undefined);
  }, [setGamePlayEvent, setGamePlayEventData]);

  const handlePrimaryAction = useCallback(() => {
    if (!lastGamePlayUrl) {
      showErrorToast("unable_to_perform_action_modal.game_not_found");
      return;
    }
    resetGameEventState();
    action === GameFailureActions.CONNECTION_LOST
      ? refetchScores?.()
      : setReplay(true);
  }, [action, lastGamePlayUrl, resetGameEventState, refetchScores]);

  const handleSecondaryAction = useCallback(() => {
    resetGameEventState();
    navigate(exitRoute);
  }, [exitRoute, navigate, resetGameEventState]);

  return (
    <Modal
      {...restProps}
      size="sm"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="unable-to-perform-action"
    >
      <div className="p-3 pb-4">
        <header style={{ flexDirection: "column" }}>
          <div className="d-flex justify-content-center w-100">
            <Image src={heroImage} width={73} height={72} alt="" />
          </div>
          <Modal.Title
            id="contained-modal-title-vcenter"
            as="h1"
            className="heading w-100 mt-3 mb-12"
          >
            {title}
          </Modal.Title>
        </header>
        <main>
          <p className="content">{content}</p>
          {action !== GameFailureActions.CONNECTION_LOST && (
            <div className="info-container d-flex w-100 justify-content-between my-3">
              <div className="info">
                <p className="info-heading">
                  {t("unable_to_perform_action_modal.you_staked")}
                </p>
                <p className="info-content">
                  {isFiatDisplay ? (
                    formatHprToFiatValue(tournament?.stakeAmount ?? 0)
                  ) : (
                    <>
                      <HyperCoin /> {tournament?.stakeAmount}
                    </>
                  )}
                </p>
              </div>
              <div className="info center-info">
                <p className="info-heading">
                  {t("unable_to_perform_action_modal.winner_gets")}
                </p>
                <p className="info-content">
                  {isFiatDisplay ? (
                    formatHprToFiatValue(tournament?.rewardAmount ?? 0)
                  ) : (
                    <>
                      <HyperCoin /> {tournament?.rewardAmount}
                    </>
                  )}
                </p>
              </div>
              <div className="info">
                <p className="info-heading">
                  {t("unable_to_perform_action_modal.replay_in")}...
                </p>
                <p className="info-content">{formatTimeString(timeLeft)}</p>
              </div>
            </div>
          )}
        </main>
        <footer className="mt-4">
          <FilledButton
            fullwidth
            size={ButtonSizes.MD}
            onClick={handlePrimaryAction}
          >
            {primaryCtaText}
          </FilledButton>
          <FilledButton
            fullwidth
            size={ButtonSizes.MD}
            className="mt-3"
            style={{ color: "#6C53E8", background: "#F5F0FF" }}
            onClick={handleSecondaryAction}
          >
            {secondaryCtaText}
          </FilledButton>
        </footer>
      </div>
      {lastGamePlayUrl && replay && (
        <GameModal
          gameLink={lastGamePlayUrl}
          gameName={tournament?.game.name}
          orientation={tournament?.game.orientation}
        />
      )}
    </Modal>
  );
}

export default UnableToPerformActionModal;

function formatTimeString(timeString?: string) {
  if (!timeString) return "";
  const [, minutes, seconds] = timeString.split(":").map(Number);
  return `${minutes}m ${seconds}s`;
}

function useCurrentContent() {
  const { t } = useTranslation("games", {
    keyPrefix: "unable_to_perform_action_modal",
  });

  const getCurrentContent = useCallback(
    (params: {
      action: GameFailureActions;
      event?: GamePlayEvents;
      data?: { recommendedPlatform?: string; recommendedOrientation?: string };
    }) => {
      const base = {
        title: t("unable_to_launch_game"),
        content: "",
        heroImage: ImageFailedLaunch,
        primaryCtaText: t("replay_for_free"),
        secondaryCtaText: t("replay_later"),
      };

      switch (params.event) {
        case GamePlayEvents.SDK_SESSION_TIMEOUT:
          return {
            ...base,
            title: t("session_timeout_title"),
            content: t("session_timeout_content"),
            heroImage: ImageSessionTimeout,
          };

        case GamePlayEvents.SDK_WRONG_PLATFORM:
          return {
            ...base,
            content: t("wrong_platform", {
              platform: params.data?.recommendedPlatform ?? "",
            }),
          };

        case GamePlayEvents.SDK_WRONG_ORIENTATION:
          return {
            ...base,
            content: t("wrong_orientation", {
              orientation: params.data?.recommendedOrientation ?? "",
            }),
          };

        case GamePlayEvents.SDK_CONNECTION_FAILED:
          return {
            ...base,
            content: t("launch_error_content"),
            heroImage: ImageFailedLaunch,
          };
      }

      if (params.action === GameFailureActions.LAUNCH_GAME) {
        return {
          ...base,
          content: t("launch_error_content"),
          heroImage: ImageCloud,
        };
      }

      if (params.action === GameFailureActions.CONNECTION_LOST) {
        return {
          title: t("connection_lost"),
          content: t("connection_lost_content"),
          heroImage: ImageNoWifi,
          primaryCtaText: t("retry"),
          secondaryCtaText: t("exit"),
        };
      }

      return {
        ...base,
        title: t("unable_to_submit_scores"),
        content: t("submit_error_content"),
        heroImage: ImageSadEmoji,
      };
    },
    [t]
  );

  return { getCurrentContent };
}
